// The command palette (Ctrl K / ⌘K): jump to any page or section of the
// dossier, or run a common action, from the keyboard. Built on cmdk, which
// filters as the user types and handles arrow-key selection.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { FileDown, FolderOpen, Languages, Plus, Search, Upload } from "lucide-react";
import { getLocale, setLocale } from "../../i18n/index.js";
import { api } from "../api/client";
import { useCurrentUser } from "../auth";
import { useDossier } from "../dossier";
import { locale, w } from "../i18n";
import { NAV_GROUPS, pagePath, shortcutLabel } from "../nav";
import { STATUS_GLYPH, STATUS_TONE, readinessLabel, readinessStatus } from "../status";

function download(url: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  link.click();
}

export function CommandPalette() {
  const { dossier, readiness, openUpload } = useDossier();
  const { local, can } = useCurrentUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(value => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const run = (action: () => void) => () => { setOpen(false); action(); };
  const go = (path: string) => run(() => navigate(path));
  const lang = locale();
  const pages = NAV_GROUPS.flatMap(group => group.pages).filter(page => !page.adminOnly || (local && can("admin")));
  const chapters = readiness.data?.chapters || [];

  return <>
    <button type="button" className="ws-search" onClick={() => setOpen(true)} aria-keyshortcuts="Control+K Meta+K">
      <Search size={14} aria-hidden="true" /><span className="ws-search-label">{w("cmd.open")}</span>
      <kbd>{shortcutLabel("K")}</kbd>
    </button>
    <Command.Dialog open={open} onOpenChange={setOpen} label={w("cmd.label")} overlayClassName="ws-overlay"
      contentClassName="ws-cmd" loop>
      <div className="ws-cmd-input"><Search size={16} aria-hidden="true" />
        <Command.Input placeholder={w("cmd.placeholder")} /></div>
      <Command.List className="ws-cmd-list">
        <Command.Empty className="ws-cmd-empty">{w("cmd.empty")}</Command.Empty>
        <Command.Group heading={w("cmd.actions")}>
          <Command.Item value={w("docs.add")} onSelect={run(() => openUpload())}>
            <Upload size={16} aria-hidden="true" />{w("docs.add")}</Command.Item>
          <Command.Item value={w("report.export")} onSelect={run(() => download(api.reportExportUrl(dossier.id, lang)))}>
            <FileDown size={16} aria-hidden="true" />{w("report.export")}</Command.Item>
          <Command.Item value={w("report.exportPdf")} onSelect={run(() => download(api.reportPdfUrl(dossier.id, lang)))}>
            <FileDown size={16} aria-hidden="true" />{w("report.exportPdf")}</Command.Item>
          <Command.Item value={w("cmd.language")} onSelect={run(() => setLocale(getLocale() === "en" ? "fr" : "en"))}>
            <Languages size={16} aria-hidden="true" />{w("cmd.language")}</Command.Item>
          <Command.Item value={w("nav.newDossier")} onSelect={go("/?new=1")}>
            <Plus size={16} aria-hidden="true" />{w("nav.newDossier")}</Command.Item>
          <Command.Item value={w("nav.allDossiers")} onSelect={go("/?pick=1")}>
            <FolderOpen size={16} aria-hidden="true" />{w("nav.allDossiers")}</Command.Item>
        </Command.Group>
        <Command.Group heading={w("cmd.pages")}>
          {pages.map(({ path, label, Icon }) => <Command.Item key={path} value={w(label)} onSelect={go(pagePath(dossier.id, path))}>
            <Icon size={16} aria-hidden="true" />{w(label)}</Command.Item>)}
        </Command.Group>
        {chapters.length > 0 && <Command.Group heading={w("cmd.sections")}>
          {chapters.flatMap(chapter => [
            <Command.Item key={chapter.code} value={w("nav.chapterTitle", { code: chapter.code, title: chapter.title })}
              onSelect={go(pagePath(dossier.id, `chapters/${chapter.code}`))}>
              <span className="ws-cmd-code">{chapter.code}</span>{w("nav.chapterTitle", { code: chapter.code, title: chapter.title })}</Command.Item>,
            ...chapter.sections.map(section => {
              const status = readinessStatus(section.status);
              return <Command.Item key={section.code} value={`${section.code} ${section.title}`}
                onSelect={go(pagePath(dossier.id, `sections/${section.code}`))}>
                <span className="ws-cmd-code">{section.code}</span><span className="ws-cmd-text">{section.title}</span>
                <span className={`ws-status ws-tone-${STATUS_TONE[status]}`}>
                  <span className="ws-status-glyph" aria-hidden="true">{STATUS_GLYPH[status]}</span>{readinessLabel(section.status)}</span>
              </Command.Item>;
            }),
          ])}
        </Command.Group>}
      </Command.List>
      <div className="ws-cmd-foot"><span><kbd>↑</kbd><kbd>↓</kbd> {w("cmd.move")}</span><span><kbd>↵</kbd> {w("cmd.select")}</span>
        <span><kbd>Esc</kbd> {w("common.close")}</span></div>
    </Command.Dialog>
  </>;
}
